import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Plus, Search, FileText, X, Eye, Edit, Trash2,
  AlertTriangle, CheckCircle, Printer, BookOpen, FolderOpen
} from 'lucide-react'
import { spkService } from '../services/spkService'
import { masterService, bookService } from '../services/masterService'
import { useAuth } from '../context/AuthContext'

function SPKListPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  
  const [spks, setSpks] = useState([])
  const [schools, setSchools] = useState([])
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ search: '', status: 'all' })
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedSPK, setSelectedSPK] = useState(null)

  useEffect(() => {
    fetchData()
  }, [filters])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [spkRes, schoolRes, booksRes] = await Promise.all([
        spkService.getAll(filters),
        masterService.getSchools(),
        masterService.getBooks()
      ])
      if (spkRes.success) setSpks(spkRes.data.spks || [])
      if (schoolRes.success) setSchools(schoolRes.data.schools || [])
      if (booksRes.success) setBooks(booksRes.data.books || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus SPK ini?')) return
    try {
      await spkService.delete(id)
      fetchData()
    } catch (err) {
      alert('Gagal menghapus SPK')
    }
  }

  const openDetailModal = (spk) => {
    setSelectedSPK(spk)
    setShowDetailModal(true)
  }

  const handleConfirmComplete = async (spk) => {
    if (!window.confirm(`Selesaikan SPK "${spk.judul}"? Status SPK akan berubah menjadi COMPLETED.`)) return
    try {
      const response = await spkService.confirmCompletion(spk.id)
      if (response.success) {
        fetchData()
      } else {
        alert(response.message || 'Gagal menyelesaikan SPK')
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message))
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-600" />
            Manajemen SPK
          </h1>
          <p className="text-gray-500">Kelola SPK, upload file, dan monitor progress</p>
        </div>
        {hasPermission('spk') && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            SPK Baru
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cari SPK..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="all">Semua Status</option>
          <option value="in_progress">Sedang Proses</option>
          <option value="completed">Selesai</option>
        </select>
      </div>

      {/* SPK Cards */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : spks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Belum ada SPK</p>
        </div>
      ) : (
        <div className="space-y-4">
          {spks.map((spk) => (
            <SPKCard
              key={spk.id}
              spk={spk}
              onDetail={openDetailModal}
              onDelete={handleDelete}
              hasPermission={hasPermission}
              onConfirmComplete={handleConfirmComplete}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSPKModal
          schools={schools}
          books={books}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchData() }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSPK && (
        <DetailSPKModal
          spk={selectedSPK}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  )
}

// SPK Card Component
function SPKCard({ spk, onDetail, onDelete, hasPermission, onConfirmComplete }) {
  const navigate = useNavigate()
  const getPriorityStyle = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      normal: 'bg-blue-100 text-blue-700 border-blue-200',
      low: 'bg-gray-100 text-gray-700 border-gray-200',
    }
    return styles[priority] || styles.normal
  }

  const getJenjangStyle = (jenjang) => {
    const styles = {
      SD: 'bg-green-500',
      SMP: 'bg-blue-500',
      SMA: 'bg-purple-500',
      SMK: 'bg-orange-500',
    }
    return styles[jenjang] || 'bg-gray-500'
  }

  const getStationLabel = (station) => {
    const labels = {
      'validasi': 'Validasi File',
      'cetak_isi': 'Cetak Isi',
      'potong_isi': 'Potong Isi',
      'susun': 'Susun',
      'cetak_cover': 'Cetak Cover',
      'qc_cover': 'QC Cover',
      'laminasi': 'Laminasi',
      'binding': 'Binding',
      'potong_sisir': 'Potong Sisir',
      'qc_potong': 'QC Potong',
      'gudang': 'Gudang',
      'done': 'Selesai',
    }
    return labels[station] || station
  }

  const customSchools = spk.customSchools || []
  const files = spk.files || []

  // Calculate file stats for compact indicator
  const expectedSlots = 2 + customSchools.reduce((sum, cs) =>
    sum + 1 + (cs.custom_type === 'cover_sisipan' ? 1 : 0), 0)
  const validatedFiles = files.filter(f => f.status === 'validated').length
  const pendingFiles = files.filter(f => f.status === 'pending').length
  const rejectedFiles = files.filter(f => f.status === 'rejected').length
  const allValidated = validatedFiles === expectedSlots && expectedSlots > 0

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-1 text-xs font-bold rounded border ${getPriorityStyle(spk.priority)}`}>
              {spk.priority?.toUpperCase()}
            </span>
            <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
              {spk.status?.toUpperCase().replace('_', ' ')}
            </span>
          </div>
          <span className={`px-3 py-1 text-sm font-bold text-white rounded ${getJenjangStyle(spk.jenjang)}`}>
            {spk.jenjang}
          </span>
        </div>
        
        <h3 className="text-xl font-bold text-gray-800 mt-2">{spk.judul}</h3>
        <p className="text-gray-500 text-sm mt-1">
          {spk.id} • {spk.kode} • {spk.qty?.toLocaleString()} eks • Deadline: {new Date(spk.deadline).toLocaleDateString('id-ID')}
        </p>
      </div>

      {/* Progress Bars */}
      <div className="px-4 py-3 bg-gray-50 border-b">
        <div className="grid grid-cols-3 gap-4">
          <ProgressItem 
            label="Isi" 
            progress={spk.progress_isi || 0} 
            station={getStationLabel(spk.station_isi)}
            color="bg-blue-500"
          />
          <ProgressItem 
            label="Cover" 
            progress={spk.progress_cover || 0} 
            station={getStationLabel(spk.station_cover)}
            color="bg-red-500"
          />
          <ProgressItem 
            label="Main" 
            progress={spk.progress_main || 0} 
            station={spk.station_main ? getStationLabel(spk.station_main) : '-'}
            color="bg-purple-500"
          />
        </div>
      </div>

      {/* Custom Schools Warning */}
      {customSchools.length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 border-b flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
          <span className="text-yellow-700 font-medium text-sm">
            {customSchools.length} Sekolah Custom: {customSchools.map(cs => cs.school_code).join(', ')}
          </span>
        </div>
      )}

      {/* File Indicator */}
      <div className="px-4 py-3 border-b">
        <button
          onClick={() => navigate(`/spk/${spk.id}/files`)}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-colors ${
            allValidated
              ? 'bg-green-50 hover:bg-green-100'
              : rejectedFiles > 0
              ? 'bg-red-50 hover:bg-red-100'
              : pendingFiles > 0
              ? 'bg-yellow-50 hover:bg-yellow-100'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <FolderOpen className={`w-5 h-5 ${
            allValidated ? 'text-green-600' : rejectedFiles > 0 ? 'text-red-600' : pendingFiles > 0 ? 'text-yellow-600' : 'text-gray-500'
          }`} />
          <div className="flex-1 flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${allValidated ? 'bg-green-500' : validatedFiles > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                style={{ width: `${expectedSlots > 0 ? (validatedFiles / expectedSlots) * 100 : 0}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {validatedFiles}/{expectedSlots} file
            </span>
            {pendingFiles > 0 && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">{pendingFiles} pending</span>
            )}
            {rejectedFiles > 0 && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">{rejectedFiles} ditolak</span>
            )}
          </div>
          <span className="text-xs text-gray-400">Kelola File &rarr;</span>
        </button>
      </div>

      {/* Completion Button */}
      {(() => {
        const allBatchesDone = spk.batches?.length > 0 && spk.batches.every(b => b.station === 'done' || b.station === 'gudang')
        const canComplete = allBatchesDone && spk.status === 'in_progress' && !spk.manager_confirmed
        return canComplete ? (
          <div className="px-4 py-2 border-b flex justify-end">
            <button
              onClick={() => onConfirmComplete(spk)}
              className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
            >
              <CheckCircle className="w-4 h-4" />
              Selesaikan SPK
            </button>
          </div>
        ) : null
      })()}

      {/* Actions */}
      <div className="px-4 py-3 flex items-center justify-between">
        <button 
          onClick={() => onDetail(spk)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <Eye className="w-4 h-4" />
          <span className="text-sm font-medium">Detail</span>
        </button>
        
        <div className="flex items-center gap-2">
          {hasPermission('spk') && spk.status !== 'completed' && (
            <Link
              to={`/spk/${spk.id}/edit`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 text-sm font-medium"
            >
              <Edit className="w-4 h-4" />
              Edit
            </Link>
          )}
          {hasPermission('spk') && (
            <button
              onClick={() => onDelete(spk.id)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Progress Item Component
function ProgressItem({ label, progress, station, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-600">{label}</span>
        <span className="text-sm text-gray-500">{progress}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs text-gray-500 mt-1">{station}</p>
    </div>
  )
}

// Create SPK Modal
function CreateSPKModal({ schools, books, onClose, onSuccess }) {
  const [selectedBook, setSelectedBook] = useState(null)
  const [formData, setFormData] = useState({
    book_id: '',
    judul: '',
    kode: '',
    jenjang: '',
    cetak_ke: 1,
    qty: '',
    deadline: '',
    priority: 'normal',
  })
  const [customSchools, setCustomSchools] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [loadingCetak, setLoadingCetak] = useState(false)

  // Handle book selection
  const handleBookSelect = async (bookId) => {
    if (!bookId) {
      setSelectedBook(null)
      setCustomSchools([])
      setFormData(prev => ({
        ...prev,
        book_id: '',
        judul: '',
        kode: '',
        jenjang: '',
        cetak_ke: 1
      }))
      return
    }

    const book = books.find(b => b.id === parseInt(bookId))
    if (book) {
      setSelectedBook(book)
      setLoadingCetak(true)

      try {
        // Get next cetak_ke + school needs in parallel
        const [cetakRes, schoolNeedsRes] = await Promise.all([
          masterService.getNextCetakKe(bookId),
          bookService.getSchoolNeeds(bookId),
        ])
        const nextCetak = cetakRes.data?.nextCetakKe || 1
        const judulWithCetak = `${book.judul} [c${nextCetak}]`

        // Auto-populate custom schools from book needs
        const autoCustomSchools = (schoolNeedsRes.data?.items || []).map(item => ({
          school_code: item.school_code,
          school_name: item.school_name,
          qty: item.qty.toString(),
          custom_type: 'cover_only',
        }))
        setCustomSchools(autoCustomSchools)

        setFormData(prev => ({
          ...prev,
          book_id: bookId,
          judul: judulWithCetak,
          kode: book.kode,
          jenjang: book.jenjang,
          cetak_ke: nextCetak
        }))
      } catch (err) {
        console.error('Error getting book data:', err)
        setCustomSchools([])
        setFormData(prev => ({
          ...prev,
          book_id: bookId,
          judul: `${book.judul} [c1]`,
          kode: book.kode,
          jenjang: book.jenjang,
          cetak_ke: 1
        }))
      } finally {
        setLoadingCetak(false)
      }
    }
  }

  // Handle cetak_ke manual change
  const handleCetakKeChange = (value) => {
    const cetakKe = parseInt(value) || 1
    if (selectedBook) {
      const judulWithCetak = `${selectedBook.judul} [c${cetakKe}]`
      setFormData(prev => ({ ...prev, cetak_ke: cetakKe, judul: judulWithCetak }))
    } else {
      setFormData(prev => ({ ...prev, cetak_ke: cetakKe }))
    }
  }

  const removeCustomSchool = (index) => {
    setCustomSchools(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.book_id) {
      setError('Pilih buku terlebih dahulu')
      return
    }
    if (!formData.qty || !formData.deadline) {
      setError('Quantity dan Deadline harus diisi')
      return
    }
    
    setSaving(true)
    setError('')
    
    try {
      const totalQty = parseInt(formData.qty)
      const customQty = customSchools.reduce((s, c) => s + (parseInt(c.qty) || 0), 0)
      const qtyReguler = totalQty - customQty
      if (qtyReguler < 0) {
        setError('Qty custom melebihi total qty')
        setSaving(false)
        return
      }
      const payload = {
        book_id: parseInt(formData.book_id),
        cetak_ke: parseInt(formData.cetak_ke) || 1,
        judul: formData.judul,
        kode: formData.kode,
        jenjang: formData.jenjang,
        qty: totalQty,
        qty_reguler: qtyReguler,
        priority: formData.priority,
        deadline: formData.deadline,
        customSchools: customSchools.map(cs => ({
          school_code: cs.school_code,
          school_name: cs.school_name,
          qty: parseInt(cs.qty),
          custom_type: cs.custom_type,
        }))
      }
      
      const response = await spkService.create(payload)
      if (response.success) {
        onSuccess()
      } else {
        setError(response.message || 'Gagal membuat SPK')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal membuat SPK')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto my-4">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-600" />
            SPK Baru
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {/* Book Selection Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pilih Buku *</label>
            <select
              value={formData.book_id}
              onChange={(e) => handleBookSelect(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Pilih dari Master Buku --</option>
              {books.filter(b => b.status === 'active').sort((a, b) => {
                const aPrinted = (a.last_cetak || 0) > 0 ? 1 : 0
                const bPrinted = (b.last_cetak || 0) > 0 ? 1 : 0
                if (aPrinted !== bPrinted) return aPrinted - bPrinted
                const aKelas = parseInt(a.kelas) || 99
                const bKelas = parseInt(b.kelas) || 99
                if (aKelas !== bKelas) return aKelas - bKelas
                return (a.judul || '').localeCompare(b.judul || '')
              }).map(book => (
                <option key={book.id} value={book.id}>
                  [{book.kode}] {book.judul} - {book.jenjang} {book.kelas ? `Kelas ${book.kelas}` : ''}
                  {book.last_cetak > 0 ? ` (sudah cetak ${book.last_cetak}x)` : ''}
                </option>
              ))}
            </select>
            {books.length === 0 && (
              <p className="text-xs text-orange-600 mt-1">Belum ada data buku. Tambahkan di Master Buku terlebih dahulu.</p>
            )}
          </div>

          {/* Selected Book Info */}
          {selectedBook && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-blue-800">📚 {selectedBook.judul}</span>
              </div>
              <div className="text-sm text-blue-600 flex flex-wrap gap-3">
                {selectedBook.mapel && <span>📘 {selectedBook.mapel}</span>}
                {selectedBook.kelas && <span>📗 Kelas {selectedBook.kelas}</span>}
                {selectedBook.jumlah_halaman && <span>📄 {selectedBook.jumlah_halaman} hal</span>}
              </div>
            </div>
          )}

          {/* Cetak Ke - Auto calculated, editable */}
          {formData.book_id && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cetakan Ke</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.cetak_ke}
                    onChange={(e) => handleCetakKeChange(e.target.value)}
                    min="1"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={loadingCetak}
                  />
                </div>
                {loadingCetak && <p className="text-xs text-gray-500 mt-1">Memuat...</p>}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Judul SPK</label>
                <input
                  type="text"
                  value={formData.judul}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>
            </div>
          )}

          {/* Kode & Jenjang - Auto filled, disabled */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kode Buku</label>
              <input
                type="text"
                value={formData.kode}
                disabled
                className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                placeholder="Otomatis dari buku"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jenjang</label>
              <input
                type="text"
                value={formData.jenjang}
                disabled
                className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                placeholder="Otomatis dari buku"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
              <input
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData(p => ({ ...p, qty: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioritas</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(p => ({ ...p, priority: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deadline *</label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData(p => ({ ...p, deadline: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {/* Custom Schools - auto from book needs */}
          {customSchools.length > 0 && (
            <div className="border rounded-lg p-4 bg-purple-50">
              <h3 className="font-medium text-purple-800 mb-3">Sekolah Custom</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-purple-700 border-b border-purple-200">
                    <th className="text-left py-1 pr-2">Sekolah</th>
                    <th className="text-right py-1 px-2 w-20">Qty</th>
                    <th className="text-center py-1 px-2 w-32">Tipe</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {customSchools.map((cs, idx) => (
                    <tr key={idx} className="border-b border-purple-100">
                      <td className="py-2 pr-2 text-gray-800">{cs.school_name || cs.school_code}</td>
                      <td className="py-2 px-2 text-right">
                        <input
                          type="number"
                          value={cs.qty}
                          onChange={(e) => {
                            const val = e.target.value
                            setCustomSchools(prev => prev.map((c, i) => i === idx ? { ...c, qty: val } : c))
                          }}
                          className="w-20 px-2 py-1 border rounded text-right text-sm"
                        />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <select
                          value={cs.custom_type}
                          onChange={(e) => {
                            const val = e.target.value
                            setCustomSchools(prev => prev.map((c, i) => i === idx ? { ...c, custom_type: val } : c))
                          }}
                          className="px-1 py-1 border rounded text-xs"
                        >
                          <option value="cover_only">Cover Only</option>
                          <option value="cover_sisipan">Cover + Sisipan</option>
                        </select>
                      </td>
                      <td className="py-2 text-center">
                        <button type="button" onClick={() => removeCustomSchool(idx)} className="text-red-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Qty Reguler */}
          {formData.qty && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="font-medium text-blue-800 text-sm">Qty Reguler (sisa)</span>
              <span className={`font-bold text-lg ${(parseInt(formData.qty) || 0) - customSchools.reduce((s, c) => s + (parseInt(c.qty) || 0), 0) < 0 ? 'text-red-600' : 'text-blue-800'}`}>
                {((parseInt(formData.qty) || 0) - customSchools.reduce((s, c) => s + (parseInt(c.qty) || 0), 0)).toLocaleString()}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving || !formData.book_id}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Menyimpan...' : 'Simpan SPK'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Detail SPK Modal
function DetailSPKModal({ spk, onClose }) {
  const customSchools = spk.customSchools || []
  const files = spk.files || []
  const totalCustomQty = customSchools.reduce((sum, cs) => sum + (cs.qty || 0), 0)
  const regulerQty = spk.qty - totalCustomQty

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            {spk.judul}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">No. SPK</p>
              <p className="font-semibold text-blue-600">{spk.id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Kode Buku</p>
              <p className="font-semibold">{spk.kode}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Jenjang</p>
              <p className="font-semibold">{spk.jenjang}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Quantity Total</p>
              <p className="font-semibold">{spk.qty?.toLocaleString()} eks</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Deadline</p>
              <p className="font-semibold">{new Date(spk.deadline).toLocaleDateString('id-ID')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="font-semibold">{spk.status}</p>
            </div>
          </div>

          {/* Custom Schools */}
          {customSchools.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                🏫 Sekolah Custom ({customSchools.length})
              </h3>
              <div className="bg-yellow-50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-yellow-100">
                    <tr>
                      <th className="text-left px-3 py-2">Kode</th>
                      <th className="text-left px-3 py-2">Nama Sekolah</th>
                      <th className="text-right px-3 py-2">Qty</th>
                      <th className="text-center px-3 py-2">Tipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customSchools.map((cs, idx) => (
                      <tr key={idx} className="border-t border-yellow-200">
                        <td className="px-3 py-2 font-medium">{cs.school_code}</td>
                        <td className="px-3 py-2">{cs.school_name}</td>
                        <td className="px-3 py-2 text-right text-green-600 font-medium">{cs.qty} eks</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            cs.custom_type === 'cover_only' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {cs.custom_type === 'cover_only' ? 'Cover Only' : 'Cover + Sisipan'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-yellow-300 font-medium">
                      <td colSpan="2" className="px-3 py-2 text-right">Total Custom:</td>
                      <td className="px-3 py-2 text-right text-green-600">{totalCustomQty} eks</td>
                      <td></td>
                    </tr>
                    <tr className="font-medium">
                      <td colSpan="2" className="px-3 py-2 text-right">Sisa Reguler:</td>
                      <td className="px-3 py-2 text-right text-blue-600">{regulerQty} eks</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Print Info */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Info Vendor Cetak
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-green-700 font-medium flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  Cetak ISI
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {spk.printInfo?.find(p => p.track_type === 'isi')?.vendor_id || 'Belum ada'}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-red-700 font-medium flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  Cetak COVER
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {spk.printInfo?.find(p => p.track_type === 'cover')?.vendor_id || 'Belum ada'}
                </p>
              </div>
            </div>
          </div>

          {/* Files */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              📁 Files ({files.length})
            </h3>
            {files.length > 0 ? (
              <div className="space-y-2">
                {files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm">
                      {file.filename} ({file.file_type})
                      {(file.version || 1) > 1 && <span className="text-xs text-gray-400 ml-1">v{file.version}</span>}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      file.status === 'validated'
                        ? 'bg-green-100 text-green-700'
                        : file.status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {file.status?.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Belum ada file</p>
            )}
          </div>

          {/* QC Results */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              🔍 QC Results ({Object.keys(spk.qc_data || {}).length})
            </h3>
            {Object.keys(spk.qc_data || {}).length > 0 ? (
              <div className="bg-gray-50 rounded-lg p-3">
                <pre className="text-xs">{JSON.stringify(spk.qc_data, null, 2)}</pre>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Belum ada hasil QC</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SPKListPage
